import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../ComponentsStyling/Header.css';
import { IconEmail, IconLinkedIn, IconGitHub } from './ChannelIcons';

/**
 * Header — sticky top navigation with section anchors and a mobile drawer.
 *
 * Renders a desktop nav strip with hover/click dropdowns and a slide-in mobile
 * panel. Cross-route section scrolling is handled by stashing a target id in
 * sessionStorage before navigation, then smooth-scrolling once the page mounts.
 */

/* External profile links shown in the "Links" dropdown (each: label, id, href,
   desc, icon). The glyphs come from the shared ChannelIcons module, which the
   Contact section also uses — these are the same three destinations, so they
   should not be drawn twice. Sizes differ per mark because LinkedIn's solid
   square fills its whole 24-unit box while GitHub's circle spans 20. */
const linksMenu = [
  {
    label: 'GitHub', id: 'github',
    href: 'https://github.com/KhaledRaed',
    desc: 'Browse my repositories',
    icon: <IconGitHub size={19} />,
  },
  {
    label: 'LinkedIn', id: 'linkedin',
    href: 'https://www.linkedin.com/in/khaled-raed-0a92412a7/',
    desc: 'Connect professionally',
    icon: <IconLinkedIn size={16} />,
  },
  {
    label: 'Email', id: 'email',
    href: 'mailto:khaledraedwork@gmail.com',
    desc: 'Drop me a message',
    icon: <IconEmail size={19} />,
  },
];

/* Primary nav items. `target`/`sectionId` drive cross-route scrolling; `menu`
   marks a dropdown. `note` is the one-line description set under each
   destination in the mobile drawer — the desktop chips ignore it, since there
   the label itself is revealed on demand and has no room for a second line. */
const NAV = [
  { key: 'home',    label: 'Home',    Icon: IcoHome,    path: '/',        menu: null,      target: '/',        sectionId: 'root',    note: 'Overview' },
  { key: 'work',    label: 'Work',    Icon: IcoWork,    path: '/work',    menu: null,      target: '/work',    sectionId: 'work',    note: 'Selected projects' },
  { key: 'about',   label: 'About',   Icon: IcoAbout,   path: '/about',   menu: null,      target: '/about',   sectionId: 'about',   note: 'Background and skills' },
  { key: 'contact', label: 'Contact', Icon: IcoContact, path: '/contact', menu: null,      target: '/contact', sectionId: 'contact', note: 'Get in touch' },
  { key: 'links',   label: 'Links',   Icon: IcoLinks,   path: null,       menu: linksMenu, target: null },
];

function IcoHome() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10.5L11 3l8 7.5V19a1 1 0 01-1 1H4a1 1 0 01-1-1v-8.5z" />
      <path d="M8 20v-7h6v7" />
    </svg>
  );
}
/* Shifted down one unit: the handle's arc reaches to y=1.2, which put the whole
   glyph a unit above the centre line its neighbours sit on. */
function IcoWork() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="16" height="11" rx="2" />
      <path d="M8 8V6a3 3 0 016 0v2" />
      <circle cx="11" cy="14" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}
function IcoAbout() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="8" r="3.5" />
      <path d="M4 19c0-3.87 3.13-7 7-7s7 3.13 7 7" />
    </svg>
  );
}
/* The envelope sat a unit and a half above the optical centre of its box and
   was noticeably shorter than the glyphs either side of it (12.6 units against
   ~17.6), which read as the icon floating high in the chip. Same envelope,
   moved down onto the centre line and given back a unit of height. */
function IcoContact() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5h14a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1z" />
      <path d="M3 5.5l8 6.5 8-6.5" />
    </svg>
  );
}
/* This is the only icon in the set that was not drawn for the 22-unit grid the
   others use: it is the standard chain glyph, whose artwork is laid out on a
   24-unit grid, and it was dropped into `viewBox="0 0 22 22"` unchanged. Its
   ink ran from 1.26,1.27 to 22.74,22.73 — past the box on all four sides — and
   an <svg> clips to its viewBox, so both ends of the chain were sliced off
   flat. The cut only read as damage on hover, because at rest the icon sits at
   42% ink and the flat edges are too faint to notice.

   The geometry below is that same chain scaled to 0.8 about the box centre
   (12,12), which lands its ink at 3.25–20.75 — inside the box, and the same
   17.5-unit optical size as the Home and Work glyphs. The stroke width is
   deliberately NOT scaled, so the line weight still matches its neighbours. */
function IcoLinks() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.4 11.8a4 4 0 006.03.43l2.4-2.4a4 4 0 00-5.66-5.66l-1.38 1.37" />
      <path d="M12.6 10.2a4 4 0 00-6.03-.43l-2.4 2.4a4 4 0 005.66 5.66l1.37-1.37" />
    </svg>
  );
}
function IcoChev() {
  return (
    <svg viewBox="0 0 8 5" fill="none" aria-hidden>
      <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

// Dropdown panel with two layouts: plain in-page links list, or richer "Links" cards.
function DropPanel({ items, visible, onSelect, isLinks }) {
  return (
    <div
      className={`hdr-drop${visible ? ' hdr-drop--open' : ''}${isLinks ? ' hdr-drop--links' : ''}`}
      role="menu"
    >
      {isLinks && (
        <div className="hdr-drop__links-head">
          <span className="hdr-drop__links-title">Find me online</span>
        </div>
      )}

      {items.map((item, i) =>
        item.href ? (
          <a
            key={item.id}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`hdr-drop__row${isLinks ? ' hdr-drop__row--link' : ''}`}
            style={{ '--i': i }}
            role="menuitem"
          >
            {isLinks && item.icon ? (
              <>
                <span className="hdr-drop__link-icon">{item.icon}</span>
                <span className="hdr-drop__link-body">
                  <span className="hdr-drop__link-label">{item.label}</span>
                  <span className="hdr-drop__link-desc">{item.desc}</span>
                </span>
              </>
            ) : (
              <>
                <span className="hdr-drop__pip" aria-hidden />
                <span className="hdr-drop__label">{item.label}</span>
              </>
            )}
            <svg className="hdr-drop__arrow" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M2 10L10 2M10 2H5M10 2v5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        ) : (
          <button
            key={item.id}
            className="hdr-drop__row"
            style={{ '--i': i }}
            onClick={() => onSelect(item.id)}
            role="menuitem"
          >
            <span className="hdr-drop__pip" aria-hidden />
            <span className="hdr-drop__label">{item.label}</span>
          </button>
        )
      )}
    </div>
  );
}

export default function Header() {
  const [scrolled,    setScrolled]    = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [openDrop,    setOpenDrop]    = useState(null);
  const timers = useRef({});

  const location = useLocation();
  const navigate  = useNavigate();

  // Toggle the condensed/scrolled header style once the page is scrolled past the top.
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    fn();
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close all menus whenever the route changes.
  useEffect(() => {
    setMobileOpen(false); setOpenDrop(null);
  }, [location.pathname]);

  // Escape closes whichever layer is open — the drawer first, then a dropdown.
  useEffect(() => {
    if (!mobileOpen && !openDrop) return;
    const onKey = e => {
      if (e.key !== 'Escape') return;
      if (mobileOpen) setMobileOpen(false);
      else setOpenDrop(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen, openDrop]);

  // A dropdown opened by click or keyboard has no pointer to leave, so the
  // hover-close never fires and it stays stuck open. Dismiss on outside press.
  useEffect(() => {
    if (!openDrop) return;
    const onDown = e => { if (!e.target.closest('.hdr-seg')) setOpenDrop(null); };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [openDrop]);

  // Drop any pending hover timer on unmount so no setState fires afterwards.
  useEffect(() => {
    const pending = timers.current;
    return () => Object.values(pending).forEach(clearTimeout);
  }, []);

  // Close the drawer if the viewport grows past the mobile breakpoint while it
  // is open — otherwise the panel stays latched over the desktop layout and
  // body scroll stays locked with no visible way to release it.
  useEffect(() => {
    if (!mobileOpen) return;
    const mq = window.matchMedia('(min-width: 769px)');
    const onChange = e => { if (e.matches) setMobileOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mobileOpen]);

  // After a cross-route navigation, scroll to the stashed section once it exists.
  // Retries a few times to wait out lazy/late-mounting content.
  useEffect(() => {
    const id = sessionStorage.getItem('scrollTarget');
    if (!id) return;
    sessionStorage.removeItem('scrollTarget');
    // Track the pending timer so a second navigation (or unmount) cancels the
    // retry chain instead of scrolling the new view to the previous target.
    let timer;
    const attempt = (n = 0) => {
      const el = document.getElementById(id);
      if (el) {
        const h = document.querySelector('.site-header')?.offsetHeight ?? 68;
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - h - 16, behavior: 'smooth' });
      } else if (n < 10) timer = setTimeout(() => attempt(n + 1), 80);
    };
    timer = setTimeout(attempt, 60);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Hover open/close with a short close delay so the cursor can travel into the panel.
  const hover   = key => { clearTimeout(timers.current[key]); setOpenDrop(key); };
  const unhover = key => { timers.current[key] = setTimeout(() => setOpenDrop(d => d === key ? null : d), 200); };

  // Scroll to a section; if it lives on another route, stash the target and navigate there first.
  const goSection = useCallback((id, targetPath) => {
    setMobileOpen(false); setOpenDrop(null);
    if (targetPath && location.pathname !== targetPath) {
      sessionStorage.setItem('scrollTarget', id);
      navigate(targetPath);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      const h = document.querySelector('.site-header')?.offsetHeight ?? 68;
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - h - 16, behavior: 'smooth' });
    }
  }, [location.pathname, navigate]);

  // Highlight the nav item matching the current route.
  const isActive = key => {
    if (key === 'home')    return location.pathname === '/';
    if (key === 'work')    return location.pathname.startsWith('/work');
    if (key === 'about')   return location.pathname.startsWith('/about');
    if (key === 'contact') return location.pathname === '/contact';
    return false;
  };

  return (
    <>
      {/* The scrim is gone with the drawer: the menu is a full-bleed sheet, so
          there is no page left behind it to dim or to tap. */}
      <header className={`site-header${scrolled ? ' site-header--scrolled' : ''}`}>

        {/* Logo */}
        <Link to="/" className="hdr-logo" aria-label="Khaled — Home">
          <div className="hdr-logo__img-wrap">
            <img
              src="/images/logos/KhaledRaedNoBk.png"
              alt="Khaled logo"
              className="hdr-logo__img"
              onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
            />
            {/* Fallback mark, shown only if the logo file fails to load */}
            <div className="hdr-logo__mark" style={{ display: 'none' }}>
              <span className="hdr-logo__k">K</span>
            </div>
          </div>
        </Link>

        {/* Desktop nav strip */}
        <nav className="hdr-strip" aria-label="Main navigation">
          <div className="hdr-strip__ambient" aria-hidden />

          {NAV.map((item, idx) => {
            const active  = isActive(item.key);
            const dropped = openDrop === item.key;
            const hasDrop = Boolean(item.menu);
            const isLinks = item.key === 'links';

            const chipCls = [
              'hdr-chip',
              active  ? 'hdr-chip--active' : '',
              dropped ? 'hdr-chip--open'   : '',
            ].filter(Boolean).join(' ');

            return (
              <React.Fragment key={item.key}>
                {idx > 0 && (
                  <div className="hdr-wire" aria-hidden>
                    <div className="hdr-wire__line" />
                    <div className="hdr-wire__node" />
                  </div>
                )}

                <div
                  className="hdr-seg"
                  onMouseEnter={() => hasDrop && hover(item.key)}
                  onMouseLeave={() => hasDrop && unhover(item.key)}
                >
                  {hasDrop ? (
                    <button
                      className={chipCls}
                      aria-haspopup="true"
                      aria-expanded={dropped}
                      aria-label={item.label}
                      onClick={() => setOpenDrop(p => p === item.key ? null : item.key)}
                      onKeyDown={e => {
                        if (e.key === 'Escape')   setOpenDrop(null);
                        if (e.key === 'ArrowDown') { e.preventDefault(); setOpenDrop(item.key); }
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenDrop(p => p === item.key ? null : item.key); }
                      }}
                    >
                      <span className="hdr-chip__icon"><item.Icon /></span>
                      <span className="hdr-chip__label">{item.label}</span>
                      <span className={`hdr-chip__chev${dropped ? ' hdr-chip__chev--open' : ''}`}>
                        <IcoChev />
                      </span>
                    </button>
                  ) : (
                    <button
                      className={chipCls}
                      aria-label={item.label}
                      onClick={() => {
                        if (item.key === 'home') {
                          navigate('/');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        } else if (item.sectionId) {
                          goSection(item.sectionId, item.target);
                        } else if (item.path) {
                          navigate(item.path);
                        }
                      }}
                    >
                      {/* The active chip carried a pulsing blue dot in its top
                          corner. The dark fill already marks it. */}
                      <span className="hdr-chip__icon"><item.Icon /></span>
                      <span className="hdr-chip__label">{item.label}</span>
                    </button>
                  )}

                  {hasDrop && (
                    <DropPanel
                      items={item.menu}
                      visible={dropped}
                      onSelect={id => goSection(id, item.target)}
                      isLinks={isLinks}
                    />
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </nav>

        {/* Right cluster: Hire Me CTA + mobile burger */}
        <div className="hdr-right">
          <button
            type="button"
            className="btn btn--primary btn--sm hdr-hire"
            onClick={() => {
              setMobileOpen(false);
              goSection('contact', '/');
            }}
          >
            Hire Me
            <svg className="btn__icon btn__icon--arrow" viewBox="0 0 16 16"
              fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {/* The bars used to morph into an X while the drawer was open. The
              drawer is min(340px, 100vw) wide and anchored right, and the
              burger sits within ~63px of that same edge at every viewport
              where it exists — so the X was always underneath the panel, and
              underneath the scrim on top of that. It was never once visible.
              The drawer's own close button is the close control. */}
          <button
            className="hdr-burger"
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* Mobile drawer: full nav with collapsible submenus */}
      <aside
        className={`hdr-mobile${mobileOpen ? ' hdr-mobile--open' : ''}`}
        aria-hidden={!mobileOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        {/* The site's own dot grid, same as the footer's — the sheet is made
            of the page's paper rather than a flat fill. */}
        <div className="hdr-mob-dots" aria-hidden="true" />

        {/* Masthead is set in tracked DM Sans caps, NOT in Fraunces. The
            destinations below are the only serif in the panel, so the name
            cannot be a small copy of them competing for the same voice. */}
        <div className="hdr-mob-head">
          <div className="hdr-mob-brand">
            <span className="hdr-mob-name">Khaled Raed</span>
            <span className="hdr-mob-role">Frontend Developer</span>
          </div>
          <button className="hdr-mob-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M1 1l12 12M13 1L1 13" />
            </svg>
          </button>
        </div>

        {/* Each destination is one full-bleed line: the name set large in
            Fraunces on the left, what is there on the right, both on the same
            baseline. The current page is the row filled with ink.

            Destinations only — the "Links" dropdown is deliberately not
            mirrored here. It was an accordion holding GitHub, LinkedIn and
            Email, sitting roughly 300px above a footer row offering the same
            three destinations again, in one panel. The footer row keeps them.

            The icons are gone with it. They were 18px glyphs beside labels
            that are always visible, so they never identified anything the word
            did not; at this size they would read as clip art beside the type. */}
        <nav className="hdr-mob-nav" aria-label="Mobile navigation">
          <ul className="hdr-mob-list" role="list">
            {NAV.filter(item => !item.menu).map((item, i) => {
              const on = isActive(item.key);
              return (
                <li key={item.key} className="hdr-mob-item" style={{ '--mi': i }}>
                  <button
                    className={`hdr-mob-link${on ? ' hdr-mob-link--on' : ''}`}
                    aria-current={on ? 'page' : undefined}
                    onClick={() => {
                      setMobileOpen(false);
                      if (item.key === 'home') {
                        navigate('/');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      } else if (item.sectionId) {
                        goSection(item.sectionId, item.target);
                      } else if (item.path) {
                        navigate(item.path);
                      }
                    }}
                  >
                    <span className="hdr-mob-word">{item.label}</span>
                    <span className="hdr-mob-note">{item.note}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="hdr-mob-foot">
          {/* Availability sits with the action it supports rather than in its
              own band under the masthead, where it was a tracked-caps line
              directly above another tracked-caps line. It is the shared
              `.avail` from App.css now — one definition site-wide — with only
              its colour changed for the dark panel. */}
          <span className="avail hdr-mob-avail">Available for freelance</span>
          {/* Was a <Link to="/contact">, which changed the URL but never
              scrolled anywhere, so the button appeared to do nothing.
              goSection navigates then scrolls, matching the desktop CTA. */}
          {/* `.btn--primary`, not `.btn--invert` — the sheet is light now, so
              the primary action is solid ink like every other primary CTA on
              the site. An inverted (white) button here would vanish. */}
          <button
            type="button"
            className="btn btn--primary btn--block"
            onClick={() => { setMobileOpen(false); goSection('contact', '/contact'); }}
          >
            Hire Me
            <svg className="btn__icon btn__icon--arrow" viewBox="0 0 16 16"
              fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="hdr-mob-links">
            <a href="https://github.com/KhaledRaed" target="_blank" rel="noopener noreferrer" className="hdr-mob-ext">GitHub</a>
            <span aria-hidden>·</span>
            <a href="https://www.linkedin.com/in/khaled-raed-0a92412a7/" target="_blank" rel="noopener noreferrer" className="hdr-mob-ext">LinkedIn</a>
            <span aria-hidden>·</span>
            <a href="mailto:khaledraedwork@gmail.com" className="hdr-mob-ext">Email</a>
          </div>
        </div>
      </aside>
    </>
  );
}